import * as React from "react";
import { Link } from "react-router-dom";
import { getAgentCard } from "@/api/client";
import { AgentCardView } from "@/api/types";
import { useSession, type Session } from "@/auth/session/SessionProvider";
import { NeuralHead } from "@/components/comic";
import { useProfileOptional } from "@/features/profile/ProfileContext";

/**
 * A user's scheduling-agent card, served at {@code /@handle}. Now GATED —
 * rendered inside AuthGate/ProfileProvider and the app shell (App wires the
 * nav + background), so only signed-in members reach it; anonymous visitors
 * hit the sign-in gate first.
 *
 * <p>Fetching waits for {@code auth.isLoading} to settle: a signed-in
 * member's token is mirrored into the token store by TokenSync, and cards
 * 404 without it — fetching too early would flash "no agent here".</p>
 *
 * <p>The 404 panel is deliberately the same for "handle doesn't exist" and
 * "card not visible to me" (the backend guarantees identical responses), so
 * card existence can't be probed.</p>
 */
export function AgentCardPage({ handle }: { handle: string }) {
  const auth = useSession();
  const [card, setCard] = React.useState<AgentCardView | null>(null);
  const [state, setState] = React.useState<"loading" | "ready" | "missing" | "error">("loading");

  React.useEffect(() => {
    if (auth.isLoading) return;
    let cancelled = false;
    getAgentCard(handle)
      .then((c) => {
        if (cancelled) return;
        setCard(c);
        setState("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState(isNotFound(err) ? "missing" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [handle, auth.isLoading]);

  // Renders inside the app shell (AppNav + background provided by App), gated to
  // signed-in members — so this is just the centred card column.
  return (
    <div className="mx-auto flex max-w-xl flex-col gap-6 px-6 py-10 font-comic-body text-foreground">
      <CardHeader handle={handle} />
      {state === "loading" && (
        <p className="text-center text-sm text-muted-foreground">Looking up this agent…</p>
      )}
      {state === "missing" && <NoAgentPanel auth={auth} />}
      {state === "error" && (
        <p className="text-center text-sm text-muted-foreground">
          Something went wrong loading this card — try again in a moment.
        </p>
      )}
      {state === "ready" && card && <CardBody card={card} auth={auth} />}
    </div>
  );
}

/** Big @handle on top — this is the address people route chats to. */
function CardHeader({ handle }: { handle: string }) {
  return (
    <header className="text-center">
      <div className="comic-shadow mx-auto inline-block rounded-xl border-2 border-black bg-brand-blue px-6 py-3 text-white">
        <h1 className="font-comic-headline text-3xl uppercase tracking-wide">@{handle}</h1>
      </div>
      <p className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
        Agent address
      </p>
    </header>
  );
}

function CardBody({
  card,
  auth,
}: {
  card: AgentCardView;
  auth: Session;
}) {
  // Your own card is reachable from the nav ("My card"), and it is the only way to see what other
  // people see — so the page stays, but the CTA does not. Asking your own agent for a meeting with
  // yourself is not an action, and the link would carry ?to=<your own handle> straight into chat.
  // Optional because the card page renders outside ProfileProvider in some shells.
  const myHandle = useProfileOptional()?.state;
  const viewerIsOwner =
    myHandle?.status === "ready" &&
    typeof myHandle.profile.handle === "string" &&
    myHandle.profile.handle.toLowerCase() === card.handle.toLowerCase();
  return (
    <main className="space-y-5">
      {/* Greeting — the agent speaking for its human. */}
      <section className="comic-shadow relative rounded-xl border-2 border-black bg-card p-6">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 shrink-0">
            <NeuralHead />
          </div>
          <div className="space-y-2 pt-1">
            <p className="font-comic-headline text-xl">
              Hi! I&apos;m {card.ownerFirstName}&apos;s agent.
            </p>
            <p className="text-sm leading-relaxed text-muted-foreground">
              I can schedule appointments on behalf of {card.ownerFirstName}
              {card.availabilityPatterns.length > 0 ? (
                <>
                  . They book{" "}
                  <span className="text-foreground">
                    {joinWithOr(card.availabilityPatterns)}
                  </span>{" "}
                  meetings.
                </>
              ) : (
                "."
              )}
            </p>
          </div>
        </div>
        {card.availabilityPatterns.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {card.availabilityPatterns.map((label) => (
              <span
                key={label}
                className="rounded-full border-2 border-black bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-wide"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Interests — only when the owner wrote some. */}
      {card.interests.length > 0 && (
        <section className="comic-shadow relative rounded-xl border-2 border-black bg-card p-6">
          <h2 className="font-comic-headline text-sm uppercase tracking-wide text-muted-foreground">
            And you could interest me in…
          </h2>
          <ul className="mt-3 space-y-2">
            {card.interests.map((interest) => (
              <li key={interest} className="flex items-start gap-2 text-sm leading-relaxed">
                <span aria-hidden className="mt-0.5 text-base">✶</span>
                <span>{interest}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* CTA — chat for members, sign-in for visitors, nothing at all for the owner. */}
      <section className="text-center">
        {viewerIsOwner ? null : auth.isAuthenticated ? (
          <Link
            to={`/chat?to=${encodeURIComponent(card.handle)}`}
            className="comic-shadow-sm inline-flex h-[48px] items-center gap-2 rounded-md border-2 border-black bg-brand-blue px-6 font-comic-headline uppercase tracking-wide text-white hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
          >
            Ask my agent for a meeting
          </Link>
        ) : (
          <SignInCta auth={auth} />
        )}
        {card.timezone && (
          <p className="mt-3 text-xs text-muted-foreground">
            My human lives in the {card.timezone} timezone.
          </p>
        )}
      </section>
    </main>
  );
}

/**
 * Neutral panel for both "no such handle" and "members-only card" — the
 * backend response is identical for the two, and so is this UI.
 */
function NoAgentPanel({ auth }: { auth: Session }) {
  return (
    <main className="comic-shadow rounded-xl border-2 border-black bg-card p-8 text-center">
      <p className="font-comic-headline text-lg uppercase">No agent here</p>
      <p className="mt-2 text-sm text-muted-foreground">
        There&apos;s no public agent card at this address.
        {!auth.isAuthenticated && " Members may see more after signing in."}
      </p>
      {!auth.isAuthenticated && (
        <div className="mt-5">
          <SignInCta auth={auth} />
        </div>
      )}
    </main>
  );
}

/** Generic by design: never names the card owner (no existence leakage). */
function SignInCta({ auth }: { auth: Session }) {
  return (
    <button
      type="button"
      onClick={() => auth.signIn()}
      className="comic-shadow-sm inline-flex h-[48px] items-center gap-2 rounded-md border-2 border-black bg-brand-blue px-6 font-comic-headline uppercase tracking-wide text-white hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
    >
      Sign in to reach an agent
    </button>
  );
}

function joinWithOr(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} or ${items[items.length - 1]}`;
}

function isNotFound(err: unknown): boolean {
  return typeof err === "object" && err !== null && "status" in err
    && (err as { status: number }).status === 404;
}
